// Technology logos, keyed by an integration type's `value` (e.g. "earth_ranger"),
// which is the filename in src/images/logos. Built once at module load from the
// bundler's require.context so adding a PNG is all it takes to show a logo.
//
// Shared by every surface that names a technology — the provider picker, the
// Connection header, the destination-type dropdown — so a logo added for one of
// them appears in all of them.
const buildLogoMap = (): Record<string, string> => {
  try {
    const context = (require as any).context(
      '../../images/logos',
      false,
      /\.png$/,
    );
    const map: Record<string, string> = {};
    context.keys().forEach((key: string) => {
      const id = key.replace('./', '').replace('.png', '');
      // CRA 5 bundles PNGs as webpack asset modules with esModule: true, so
      // require.context returns the module namespace ({ default: url }), not
      // the URL. Rendering that object gave every logo src="[object Object]".
      // Unwrap defensively so a non-esModule bundler still works.
      const mod = context(key);
      map[id] = (mod && mod.default) || mod;
    });
    return map;
  } catch {
    // No require.context (jest, or a non-webpack bundler): callers render
    // their no-logo fallback instead.
    return {};
  }
};

const logoMap = buildLogoMap();

// One vendor can reach the grid under more than one `value`: a Gundi v1 entry
// hard-coded in gundiV1Technologies.tsx alongside its v2 type, or two type
// records for the same product (a pull half and a push half). They point at one
// PNG rather than shipping the same bytes twice.
const LOGO_ALIASES: Record<string, string> = {
  // Gundi v1 entries whose v2 counterpart already has the artwork.
  er2er: 'earth_ranger',
  sigfox: 'sigfox_iet10_trackers',
  'track-solid': 'tracksolid',
  'receiving-data-from-smart-connect': 'smart_connect',
  // Same vendor, two type records.
  wps_watch_pull: 'wps_watch',
  hytera_smartone_dispatch: 'hytera_smartdispatchplus',
  tracksolidpro: 'tracksolid',
};

export const getTechnologyLogo = (typeValue?: string): string | null => {
  if (!typeValue) return null;
  return logoMap[LOGO_ALIASES[typeValue] ?? typeValue] || null;
};
